/**
 * Belgian structured communication ("gestructureerde mededeling" /
 * "communication structurée").
 *
 * Printed as +++090/9337/55493+++ — twelve digits, where the last two are a
 * check digit over the first ten: first10 mod 97, and 97 when the remainder
 * is 0 (never 00).
 *
 * Worth more than it looks. This is simultaneously the field OCR most often
 * mangles (long run of digits, slashes, plus signs) and the field with the
 * worst consequence when wrong — a payment with a broken reference reaches
 * the supplier's account but not their invoice, so they chase you for money
 * you already paid. A free deterministic check on exactly that field is the
 * best value in the whole validator set.
 */

export type ReferenceCheck =
  | { valid: true; canonical: string; formatted: string }
  | { valid: false; reason: ReferenceFailure; canonical?: string }

export type ReferenceFailure =
  | 'empty'
  | 'wrong_length'
  | 'not_numeric'
  | 'checksum_failed'

/** Strip everything decorative, leaving the twelve digits. */
export function normaliseReference(input: string): string {
  return input.replace(/[^0-9]/g, '')
}

/**
 * Does this look like it is *meant* to be a Belgian structured communication?
 *
 * The distinction matters because the two kinds of reference fail differently.
 * A structured communication carries its own check digits, so a wrong one is
 * provably wrong and must stop the payment — the supplier's system will never
 * match it. A free-text reference ("Vermeld factuur F2026-084417") has nothing
 * to check against and is perfectly valid; running mod-97 on it and flagging
 * the failure means flagging correct invoices, which is how a warning becomes
 * something people click past. See docs/qr-belgium.md §4.
 *
 * So: delimiters, or exactly twelve digits and nothing alphabetic. Anything
 * else is free text and is left alone.
 */
export function looksStructured(input: string | null | undefined): boolean {
  if (!input) return false
  const trimmed = input.trim()
  if (/[+*]{3}/.test(trimmed)) return true
  if (/[a-zA-Z]/.test(trimmed)) return false
  return normaliseReference(trimmed).length === 12
}

export function validateStructuredCommunication(
  input: string | null | undefined,
): ReferenceCheck {
  if (!input || !input.trim()) return { valid: false, reason: 'empty' }

  // Reject anything containing letters before stripping, otherwise "REF 12345"
  // would silently become a numeric reference.
  if (/[a-zA-Z]/.test(input)) return { valid: false, reason: 'not_numeric' }

  const digits = normaliseReference(input)
  if (digits.length !== 12) {
    return { valid: false, reason: 'wrong_length', canonical: digits }
  }

  const body = digits.slice(0, 10)
  const given = Number(digits.slice(10, 12))

  const remainder = Number(body) % 97
  const expected = remainder === 0 ? 97 : remainder

  if (given !== expected) {
    return { valid: false, reason: 'checksum_failed', canonical: digits }
  }

  return { valid: true, canonical: digits, formatted: formatReference(digits) }
}

/** Back to +++DDD/DDDD/DDDDD+++ for display and for the QR payload. */
export function formatReference(canonical: string): string {
  const d = normaliseReference(canonical)
  if (d.length !== 12) return canonical
  return `+++${d.slice(0, 3)}/${d.slice(3, 7)}/${d.slice(7, 12)}+++`
}

/** Generate a valid reference from a ten-digit body. Useful in tests and seeds. */
export function buildStructuredCommunication(body: string): string {
  const digits = normaliseReference(body).padStart(10, '0').slice(-10)
  const remainder = Number(digits) % 97
  const check = remainder === 0 ? 97 : remainder
  return formatReference(digits + String(check).padStart(2, '0'))
}

/**
 * ISO 11649 creditor reference (RF...). Different standard, different field in
 * the EPC QR payload. Belgian domestic invoices generally do NOT use these —
 * which is precisely why the +++ reference must go in the *unstructured*
 * remittance field. Detecting the difference is what stops us putting it in
 * the wrong one.
 */
export function isIso11649(input: string): boolean {
  const s = input.replace(/\s/g, '').toUpperCase()
  if (!/^RF\d{2}[A-Z0-9]{1,21}$/.test(s)) return false

  const moved = s.slice(4) + s.slice(0, 4)
  let numeric = ''
  for (const ch of moved) {
    const code = ch.charCodeAt(0)
    numeric += code >= 65 && code <= 90 ? String(code - 55) : ch
  }
  let remainder = 0
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 7)) % 97
  }
  return remainder === 1
}
